import React, { useEffect, useMemo, useState } from 'react';
import { Anchor, Button, Checkbox, FieldController } from '@keplerco/core';
import { FieldValues, useForm } from 'react-hook-form';
import classNames from 'classnames';
import layoutStyles from '../../../../design/layout.module.css';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { WidgetCard } from '../../../analytics/analytics-filters/widget-card';
import assessmentsWizardStyles from '../../assessments-wizard.module.css';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { ISettingCheckboxProps, ISettingsStepLayoutProps } from './settings-step.models';
import styles from './settings-step.module.css';
import { ExitModal } from '../../../../components/modals/exit.modal';
import { AssessmentRequest, PeerAssessmentRequest } from '../../../../overmind/actions';
import { useAppActions, useAppState } from '../../../../overmind';
import { PagePath } from '../../../../navigation/navigation.enums';
import { NotificationPriority, NotificationType } from '../../../../notifications/notifications.models';

function SettingCheckbox({ name, control, label, description }: ISettingCheckboxProps) {
  return (
    <React.Fragment>
      <FieldController
        name={name}
        control={control}
        render={({ field }) => (
          <Checkbox clickablearea="label" {...field} checked={field.value} id={name}>
            {label}
          </Checkbox>
        )}
      />
      <p style={{ marginLeft: 30, color: 'var(--text_1)' }}>{description}</p>
    </React.Fragment>
  );
}

export function SettingsStepLayout({ isLoading, setIsLoading, onBack, onNext, ...props }: ISettingsStepLayoutProps): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const defaultValues: FieldValues = useMemo(
    () => ({
      forceAssessment: props.assessment.forceAssessment,
      isWeightingEnabled: props.peerAssessment.isWeightingEnabled,
      isAnonymous: props.peerAssessment.isAnonymous,
      isAssessmentFeedbackEnabled: props.peerAssessment.isAssessmentFeedbackEnabled,
      isSkillsFeedbackEnabled: props.peerAssessment.isSkillsFeedbackEnabled,
    }),
    [props.assessment, props.peerAssessment]
  );

  const { control, handleSubmit, watch, formState, trigger } = useForm({ mode: 'onBlur', defaultValues });

  const [fieldValues, setFieldValues] = useState<FieldValues>();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(formState.isDirty);
  const [isSaveDraft, setIsSaveDraft] = useState<boolean | null>(null);
  const [assessmentRequest, setAssessmentRequest] = useState<AssessmentRequest | null>(null);
  const [peerAssessmentRequest, setPeerAssessmentRequest] = useState<PeerAssessmentRequest | null>(null);

  useEffect(() => {
    if (isSaveDraft === null || !assessmentRequest || !peerAssessmentRequest) return;

    if (isSaveDraft) {
      actions.dismissAllNotifications();
      actions.addNotification({
        id: crypto.randomUUID(),
        active: true,
        priority: NotificationPriority.Toast,
        toastIncludeIcon: true,
        type: NotificationType.Warn,
        title: 'This assessment is now a draft',
        message: 'You will need to schedule this once your changes are complete',
      });
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`);
    } else {
      onNext({ assessment: assessmentRequest, peerAssessment: { ...peerAssessmentRequest, assignees: props.peerAssessment.assignees } });
    }

    setIsLoading(false);
    setIsSaveDraft(null);
    setAssessmentRequest(null);
    setPeerAssessmentRequest(null);
  }, [isSaveDraft, assessmentRequest, peerAssessmentRequest]);

  useEffect(() => {
    if (!fieldValues) setFieldValues(defaultValues);
    watch(setFieldValues);
  }, []);

  useEffect(() => {
    setHasUnsavedChanges(formState.isDirty);
  }, [formState.isDirty]);

  async function saveSettings(isSaveDraft?: boolean) {
    setIsLoading(true);

    const assessmentRequest: AssessmentRequest = {
      ...props.assessment,
      forceAssessment: fieldValues?.forceAssessment,
    };
    setAssessmentRequest(assessmentRequest);
    await actions.updateAssessment(assessmentRequest);

    const peerAssessmentRequest: PeerAssessmentRequest = {
      ...props.peerAssessment,
      isWeightingEnabled: fieldValues?.isWeightingEnabled,
      isAnonymous: fieldValues?.isAnonymous,
      isAssessmentFeedbackEnabled: fieldValues?.isAssessmentFeedbackEnabled,
      isSkillsFeedbackEnabled: fieldValues?.isSkillsFeedbackEnabled,
      assignees: props.peerAssessment.assignees.map(assignee => ({
        ...assignee,
        reviewers: assignee.reviewers.map(reviewer => ({ learnerSlug: reviewer.learnerSlug, isManager: reviewer.isManager })),
        companySkillIds: assignee.companySkills.map(companySkill => companySkill.id),
      })),
    };
    setPeerAssessmentRequest(peerAssessmentRequest);
    await actions.updatePeerAssessment({ assessmentSlug: props.assessment.slug!, peerAssessment: peerAssessmentRequest });

    setHasUnsavedChanges(false);
    setIsSaveDraft(isSaveDraft ?? false);
  }

  return (
    <React.Fragment>
      <form id="assessmentSettings" className={assessmentsWizardStyles.stepContainer} onSubmit={handleSubmit(() => saveSettings())}>
        <SkeletonOverlay isLoading={isLoading}>
          <WidgetCard title="Settings" subtitle="Select which options you'd like to enable for this assessment">
            <div className={classNames('card', styles.card)}>
              <h4>Open ended feedback:</h4>

              <div className={styles.grid}>
                <SettingCheckbox name="isSkillsFeedbackEnabled" control={control} label="Skills feedback" description="Allow assessors to comment on each skill they rate" />
                <SettingCheckbox name="isAssessmentFeedbackEnabled" control={control} label="Assessment feedback" description="Allow assessors to give overall feedback on the assessment subjects" />
                <SettingCheckbox name="isAnonymous" control={control} label="Anonymise feedback" description="Hide assessor names in comments and ratings visible to the assessment subject" />
              </div>
            </div>

            <div className={classNames('card', styles.card)}>
              <h4>Scoring:</h4>

              <div className={styles.grid}>
                <SettingCheckbox name="isWeightingEnabled" control={control} label="Enable weightings" description="Give manager ratings more weight than other assessors" />
              </div>
            </div>

            <div className={classNames('card', styles.card)}>
              <h4>Completion rules:</h4>

              <div className={styles.grid}>
                <SettingCheckbox name="forceAssessment" control={control} label="Require completion on login" description="Assessors will be shown the assessment as soon as they log in. They must complete it before accessing any other part of the platform" />
              </div>
            </div>
          </WidgetCard>
        </SkeletonOverlay>

        <footer className={classNames('card', layoutStyles.wizardFooter)}>
          <Anchor reverse arrow aria-label="Return to previous step" onClick={onBack}>
            Back
          </Anchor>

          <div className={assessmentsWizardStyles.stepFooterButtonGroup}>
            <Anchor
              aria-label="Save as draft and exit"
              onClick={async () => {
                const isValid = await trigger();
                if (!isValid) return;
                return saveSettings(true);
              }}
            >
              Save draft
            </Anchor>

            <Button disabled={isLoading} filled type="button" arrow aria-label="Save and continue to next step">
              Next
            </Button>
          </div>
        </footer>
      </form>

      <ExitModal hasUnsavedChanges={hasUnsavedChanges} />
    </React.Fragment>
  );
}
